import { Link } from 'react-router-dom'
import { GlassCard } from '@/components/ui/glass-card'
import { ArrowLeft } from 'lucide-react'

export default function CalculatorsPage() {
  const calculators = [
    {
      href: '/resources/calculators/mortgage-payment',
      slug: 'mortgage-payment',
      title: 'Mortgage Payment',
      description:
        'Calculate your monthly principal and interest payments based on loan amount, interest rate, and term.',
    },
    {
      href: '/resources/calculators/cash-on-cash',
      slug: 'cash-on-cash',
      title: 'Cash-on-Cash Return',
      description: 'Measure your annual return on the actual cash you invested in a property.',
    },
    {
      href: '/resources/calculators/dscr',
      slug: 'dscr',
      title: 'DSCR Calculator',
      description: 'Determine if your rental income adequately covers your debt obligations.',
    },
    {
      href: '/resources/calculators/rent-vs-buy',
      slug: 'rent-vs-buy',
      title: 'Rent vs Buy',
      description: 'Compare the long-term costs of renting versus buying a property.',
    },
    {
      href: '/resources/calculators/brrrr',
      slug: 'brrrr',
      title: 'BRRRR Strategy',
      description: 'Model returns using the Buy, Rehab, Rent, Refinance, Repeat strategy.',
    },
  ]

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="border-border border-b">
        <div className="mx-auto max-w-5xl px-5 pt-10 pb-12 md:px-8 md:pt-14 md:pb-16">
          <Link
            to="/resources"
            className="text-muted-foreground hover:text-navy mb-8 inline-flex items-center gap-2 text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Resources
          </Link>
          <h1 className="text-fluid-h1 text-navy mb-6 max-w-4xl">Investment calculators</h1>
          <p className="text-fluid-body-lg text-muted-foreground max-w-2xl">
            The same formulas the firm uses to screen acquisitions, for your own analysis.
          </p>
        </div>
      </section>

      {/* Calculators Grid */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {calculators.map((calc) => {
              return (
                <Link key={calc.href} to={calc.href} className="calc-card group">
                  <GlassCard hover className="h-full">
                    <h3 className="text-foreground group-hover:text-primary mb-3 text-xl font-semibold transition-colors">
                      {calc.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{calc.description}</p>
                  </GlassCard>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <GlassCard accent>
            <h2 className="text-fluid-h3 text-foreground mb-6">How to Use These Calculators</h2>
            <div className="text-muted-foreground space-y-4">
              <p>
                These calculators are designed to give you a quick analysis of potential deals. They use standard
                formulas and assumptions common in real estate investing.
              </p>
              <p>
                <span className="text-foreground font-medium">Important note:</span> These tools provide estimates only.
                Always verify numbers with your lender, accountant, and other professionals before making investment
                decisions.
              </p>
              <p>
                For a comprehensive deal analysis, we recommend using multiple calculators together to understand
                different aspects of a potential investment.
              </p>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
